import { useEffect, useLayoutEffect } from 'react';
import { useMusicState } from '../storeHooks/useMusicState';

interface UseUserInteractionProps {
  userHasInteracted: boolean;
  pendingPlay: boolean;
  setUserHasInteracted: (interacted: boolean) => void;
  setPendingPlay: (pending: boolean) => void;
}

export const useUserInteraction = ({
  userHasInteracted,
  setUserHasInteracted,
  setPendingPlay,
}: UseUserInteractionProps) => {
  const { activePlaylist } = useMusicState();

  // Detect if music player was opened via user interaction (clicking music)
  // Using useLayoutEffect to ensure pendingPlay is set BEFORE useAudioLoader's useEffect runs
  // This fixes the race condition where audio could load before pendingPlay was set to true
  useLayoutEffect(() => {
    if (activePlaylist) {
      // If music player opens with a playlist, user has already interacted
      if (!userHasInteracted) {
        setUserHasInteracted(true);
      }
      // Always set pending play to trigger auto-play when audio is ready
      // This ensures audio plays when selecting a new album
      setPendingPlay(true);
    }
  }, [activePlaylist?.album.id, activePlaylist?.currentIndex]);

  // Add touch/click event listeners to detect user interaction on mobile (for direct URL access)
  useEffect(() => {
    if (userHasInteracted) return;

    const handleUserInteraction = () => {
      if (!userHasInteracted) {
        setUserHasInteracted(true);
      }
    };

    // Add event listeners for various user interactions
    document.addEventListener('touchstart', handleUserInteraction, {
      once: true,
      passive: true,
    });
    document.addEventListener('touchend', handleUserInteraction, {
      once: true,
      passive: true,
    });
    document.addEventListener('click', handleUserInteraction, {
      once: true,
      passive: true,
    });
    document.addEventListener('keydown', handleUserInteraction, {
      once: true,
      passive: true,
    });

    return () => {
      document.removeEventListener('touchstart', handleUserInteraction);
      document.removeEventListener('touchend', handleUserInteraction);
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
    };
  }, [userHasInteracted]);
};
